import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import './styles/tokens.css';
import './styles/fonts.css';
import './styles/app.css';

import { App } from './app';
import { AuthProvider } from './auth';
import { initErrorReporting } from './observability';

/**
 * **A router, since 2026-09-10; still no query client.** The operator surface
 * stopped being one page when the org detail moved off the bottom of the list
 * and onto an address of its own — a screen an operator can link to, reload, and
 * come back to on the tab they left. `react-router-dom` is the dashboard's own
 * version, which is the whole reason it is the dependency taken: a second
 * routing idea across two apps in one workspace is a second thing to keep true.
 *
 * The router goes **inside** `AuthProvider`, which is the dashboard's own
 * arrangement (`dashboard/src/main.tsx`) and matters for one case: in Auth0 mode
 * the reader lands on `/callback`, and the provider puts the address bar back
 * where they were with `history.replaceState` once the code exchange finishes.
 * `BrowserRouter` reads `window.location` when it mounts and hears nothing about
 * a later `replaceState` — so it has to mount *after* the exchange. Below the
 * provider it does, because until then the provider is rendering a sign-in
 * screen and not its children at all. `/callback` is still a route (app.tsx) for
 * the reader who reloads that address with a live session and no code to
 * exchange.
 *
 * `AuthProvider` is otherwise the one wrapper, and in dev mode it is not even
 * that — it renders its children and mounts nothing (src/auth.tsx).
 */
initErrorReporting();

const container = document.getElementById('root');
if (!container) throw new Error('#root is missing from index.html');

createRoot(container).render(
  <StrictMode>
    <AuthProvider>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </AuthProvider>
  </StrictMode>,
);
